import { useEntitlements } from '@gm-commercial/entitlements-client';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '~/app/store';
import {
  BDRequestStatus,
  BDRequestType,
} from '~/common/models/apis/apiResponse.model';
import { FeatureCodes } from '~/common/models/plans.model';
import { selectActingProfile } from '~/store/profile/profileSlice.selectors';
import { fetchCamerasSettings } from '~/store/settings/camerasSettingsSlice';
import {
  getCamerasSettingsOperationStatus,
  selectCamerasSettingsFetchContext,
} from '~/store/settings/camerasSettingsSlice.selectors';

type CamerasSettingsAvailability = {
  /** True when the camera settings fetch failed, i.e. the org has no cameras. */
  isCameraSettingsFailed: boolean;
  /** True only after camera settings resolve successfully for the current profile context. */
  hasCameras: boolean;
  /** True while the camera settings request is in flight. */
  isLoading: boolean;
  /**
   * Whether camera-dependent UI should render. `false` while the check has not
   * resolved yet and `false` once it has failed (the org has no cameras), so
   * camera surfaces never flash in and then disappear.
   *
   * `true` when the check is skipped (no Telematics feature, no organization id,
   * or `enabled: false`): camera availability is unknown, so the caller's own
   * entitlement gating applies instead.
   */
  showCameraSurfaces: boolean;
};

type UseCamerasSettingsAvailabilityOptions = {
  /**
   * When `false`, the hook reads the existing camera-settings status without
   * dispatching its own fetch. Use this for embedded usages where a parent
   * already triggers the fetch, to avoid a duplicate request. Defaults to `true`.
   */
  enabled?: boolean;
};

/**
 * Fetches camera settings for the current profile and reports whether the org
 * has cameras. A `FAILED` GET status is treated as "the org has no cameras",
 * which callers use to hide camera-only UI (e.g. the Camera settings tab and
 * video-based safety subtypes).
 *
 * The fetch is skipped when the org lacks the Telematics (TELE) feature, when no
 * organization id is available yet, or when `enabled` is `false`. The returned
 * flags are only meaningful while a fetch is attempted, so they report `false`
 * when fetching is skipped (rather than surfacing a stale/never-resolving state).
 *
 * Once the check resolves for a given profile context, that result is retained
 * across later re-fetches. Camera settings are re-fetched whenever a camera
 * setting is updated, which resets the shared request status to `PENDING`;
 * without this latch every dependent surface would blink out and back on each
 * time. The latch is keyed on the profile context so switching org, hub, or
 * fleet re-enters the loading state instead of answering with the previous
 * context's result.
 * @returns Availability flags for camera settings.
 */
export const useCamerasSettingsAvailability = ({
  enabled = true,
}: UseCamerasSettingsAvailabilityOptions = {}): CamerasSettingsAvailability => {
  const dispatch = useAppDispatch();
  const { hasFeature } = useEntitlements();
  const hasTelematicsFeatureAccess = hasFeature(FeatureCodes.TELE);
  const profile = useSelector(selectActingProfile);
  const organizationsId = profile?.organization?.id;
  const hubsId = profile?.hub?.id;
  const fleetsId = profile?.fleet?.id;

  const canFetch =
    enabled && hasTelematicsFeatureAccess && Boolean(organizationsId);

  const { status } = useSelector(
    getCamerasSettingsOperationStatus(BDRequestType.GET_ALL)
  );
  const fetchContext = useSelector(selectCamerasSettingsFetchContext);

  const dispatchFetchCamerasSettings = useCallback(
    ({
      organizationsId,
      hubsId,
      fleetsId,
    }: {
      organizationsId?: string;
      hubsId?: string;
      fleetsId?: string;
    }) => {
      dispatch(
        fetchCamerasSettings({
          organizationsId,
          hubsId,
          fleetsId,
        })
      );
    },
    [dispatch]
  );

  useEffect(() => {
    if (!canFetch) return;
    dispatchFetchCamerasSettings({ organizationsId, hubsId, fleetsId });
  }, [
    canFetch,
    dispatchFetchCamerasSettings,
    organizationsId,
    hubsId,
    fleetsId,
  ]);

  // Identifies the profile context the latched result belongs to, so a result
  // from a previous org/hub/fleet is never reused after a context switch.
  const contextKey = `${organizationsId ?? ''}|${hubsId ?? ''}|${fleetsId ?? ''}`;
  const [lastResolved, setLastResolved] = useState<{
    key: string;
    status: BDRequestStatus;
  }>();

  const isCurrentContext =
    fetchContext?.organizationsId === organizationsId &&
    fetchContext?.hubsId === hubsId &&
    fetchContext?.fleetsId === fleetsId;
  const isTerminal =
    isCurrentContext &&
    (status === BDRequestStatus.SUCCEEDED || status === BDRequestStatus.FAILED);

  useEffect(() => {
    if (!isTerminal) return;
    setLastResolved({ key: contextKey, status });
  }, [isTerminal, status, contextKey]);

  // A live terminal status always wins; otherwise fall back to the last result
  // resolved for this same context. `undefined` means "not answered yet".
  const resolvedStatus = isTerminal
    ? status
    : lastResolved?.key === contextKey
      ? lastResolved.status
      : undefined;

  const isCameraSettingsFailed =
    canFetch && resolvedStatus === BDRequestStatus.FAILED;
  const hasCameras = canFetch && resolvedStatus === BDRequestStatus.SUCCEEDED;
  const isLoading = canFetch && resolvedStatus === undefined;
  const showCameraSurfaces = !isLoading && !isCameraSettingsFailed;

  return {
    isCameraSettingsFailed,
    hasCameras,
    isLoading,
    showCameraSurfaces,
  };
};
